import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { Sparkles, Users, Clock, TrendingUp, BookOpen, BarChart3, Settings, Search, ChevronDown, ChevronUp, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import UnschoolingContentManager from '../../components/admin/UnschoolingContentManager';
import UnschoolingCategoryManager from '../../components/admin/UnschoolingCategoryManager';

const UnschoolingManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalSessions: 0,
    activeSessions: 0,
    completedSessions: 0,
    totalTimeSpent: 0,
    totalInteractions: 0,
    uniqueUsers: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const sessionsRef = collection(db, 'unschooling_sessions');
      const sessionsSnap = await getDocs(sessionsRef);

      let totalTime = 0;
      let totalInteractions = 0;
      let active = 0;
      let completed = 0;

      sessionsSnap.forEach(doc => {
        const data = doc.data();
        totalTime += data.totalTimeSpent || 0;
        totalInteractions += (data.interactions || []).length;
        if (data.status === 'active') active++;
        if (data.status === 'completed') completed++;
      });

      setStats({
        totalSessions: sessionsSnap.size,
        activeSessions: active,
        completedSessions: completed,
        totalTimeSpent: totalTime,
        totalInteractions: totalInteractions,
        uniqueUsers: sessionsSnap.size
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'categories', name: 'Categories & Topics', icon: BookOpen },
    { id: 'content', name: 'Content Library', icon: BookOpen },
    { id: 'students', name: 'Student Analytics', icon: Users },
    { id: 'sessions', name: 'Active Sessions', icon: Users },
    { id: 'analytics', name: 'Analytics', icon: TrendingUp },
    { id: 'settings', name: 'Settings', icon: Settings }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
            <Sparkles className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Unschooling Mode Management
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Manage content, monitor sessions, and view analytics
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Sessions</span>
              <Users className="text-blue-500" size={20} />
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {stats.totalSessions}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.activeSessions} active, {stats.completedSessions} completed
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Time</span>
              <Clock className="text-green-500" size={20} />
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {Math.floor(stats.totalTimeSpent / 60)}h
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Avg: {stats.totalSessions > 0 ? Math.floor(stats.totalTimeSpent / stats.totalSessions) : 0} min/session
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Interactions</span>
              <TrendingUp className="text-purple-500" size={20} />
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {stats.totalInteractions}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Avg: {stats.totalSessions > 0 ? Math.floor(stats.totalInteractions / stats.totalSessions) : 0} per session
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Unique Users</span>
              <Sparkles className="text-yellow-500" size={20} />
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {stats.uniqueUsers}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Exploring their interests
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                    ${activeTab === tab.id
                      ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }
                  `}
                >
                  <Icon size={18} />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && <OverviewTab stats={stats} />}
          {activeTab === 'categories' && <CategoriesTab />}
          {activeTab === 'content' && <ContentLibraryTab />}
          {activeTab === 'students' && <StudentAnalyticsTab />}
          {activeTab === 'sessions' && <ActiveSessionsTab />}
          {activeTab === 'analytics' && <AnalyticsTab />}
          {activeTab === 'settings' && <SettingsTab />}
        </div>
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ stats }) => {

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Overview
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Unschooling Mode allows students to explore their interests freely for 14 days. 
          Here's a snapshot of how students are using this feature.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
            Engagement Rate
          </h4>
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
            {stats.activeSessions > 0 ? Math.round((stats.activeSessions / stats.totalSessions) * 100) : 0}%
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            of sessions are currently active
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
            Completion Rate
          </h4>
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
            {stats.totalSessions > 0 ? Math.round((stats.completedSessions / stats.totalSessions) * 100) : 0}%
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            of students completed 14 days
          </p>
        </div>
      </div>

      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
              Next Steps
            </h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Review content library and add more unique items</li>
              <li>• Check analytics to understand student preferences</li>
              <li>• Export reports for career counseling integration</li>
              <li>• Monitor which topics are most popular</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Categories Tab
const CategoriesTab = () => {
  return <UnschoolingCategoryManager />;
};

// Content Library Tab
const ContentLibraryTab = () => {
  return <UnschoolingContentManager />;
};

// Active Sessions Tab
const ActiveSessionsTab = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const sessionsRef = collection(db, 'unschooling_sessions');
      const q = query(sessionsRef, orderBy('startDate', 'desc'));
      const sessionsSnap = await getDocs(q);
      
      const sessionsList = [];
      for (const docSnap of sessionsSnap.docs) {
        const sessionData = docSnap.data();
        
        // Only include active sessions
        if (sessionData.status !== 'active') continue;
        
        // Get user data
        try {
          const userRef = doc(db, 'users', sessionData.userId);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.exists() ? userDoc.data() : null;
          
          sessionsList.push({
            id: docSnap.id,
            ...sessionData,
            userName: userData?.displayName || userData?.name || 'Unknown User',
            userEmail: userData?.email || ''
          });
        } catch (userError) {
          console.error('Error fetching user data:', userError);
          sessionsList.push({
            id: docSnap.id,
            ...sessionData,
            userName: 'Unknown User',
            userEmail: ''
          });
        }
      }
      
      setSessions(sessionsList);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {sessions.length} active sessions
      </p>
      
      {sessions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <Users className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Active Sessions
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            No students are currently exploring in Unschooling Mode
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sessions.map((session) => {
            const daysElapsed = Math.floor((Date.now() - new Date(session.startDate).getTime()) / (24 * 60 * 60 * 1000));
            const daysRemaining = Math.max(0, 14 - daysElapsed);
            
            return (
              <div key={session.id} className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {session.userName}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {session.userEmail}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm rounded-full">
                    Active
                  </span>
                </div>
                
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {Math.floor((session.totalTimeSpent || 0) / 60)}h
                    </div>
                    <div className="text-xs text-gray-500">Time Spent</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {(session.interactions || []).length}
                    </div>
                    <div className="text-xs text-gray-500">Interactions</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {daysElapsed}
                    </div>
                    <div className="text-xs text-gray-500">Days Elapsed</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {daysRemaining}
                    </div>
                    <div className="text-xs text-gray-500">Days Left</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Analytics Tab
const AnalyticsTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const sessionsRef = collection(db, 'unschooling_sessions');
      const sessionsSnap = await getDocs(sessionsRef);

      const categoryCount = {};
      const topicCount = {};
      const contentTypeCount = {};

      sessionsSnap.forEach(doc => {
        const data = doc.data();
        (data.interactions || []).forEach(interaction => {
          if (interaction.category) {
            categoryCount[interaction.category] = (categoryCount[interaction.category] || 0) + 1;
          }
          if (interaction.topic) {
            topicCount[interaction.topic] = (topicCount[interaction.topic] || 0) + 1;
          }
          if (interaction.contentType) {
            contentTypeCount[interaction.contentType] = (contentTypeCount[interaction.contentType] || 0) + 1;
          }
        });
      });

      const sortedCategories = Object.entries(categoryCount).sort((a, b) => b[1] - a[1]);
      const sortedTopics = Object.entries(topicCount).sort((a, b) => b[1] - a[1]).slice(0, 10);

      setAnalytics({
        categoryDistribution: sortedCategories,
        topTopics: sortedTopics,
        contentTypePreferences: contentTypeCount
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <TrendingUp className="mx-auto text-gray-400 mb-4" size={48} />
        <p className="text-gray-600 dark:text-gray-400">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
          Category Distribution
        </h4>
        <div className="space-y-3">
          {analytics.categoryDistribution.map(([category, count]) => {
            const total = analytics.categoryDistribution.reduce((sum, [, c]) => sum + c, 0);
            const percentage = Math.round((count / total) * 100);
            
            return (
              <div key={category}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{category}</span>
                  <span className="text-sm text-gray-500">{count} interactions ({percentage}%)</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
          Top 10 Topics
        </h4>
        <div className="grid grid-cols-2 gap-4">
          {analytics.topTopics.map(([topic, count], index) => (
            <div key={topic} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  #{index + 1}
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">{topic}</div>
                  <div className="text-sm text-gray-500">{count} interactions</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
          Content Type Preferences
        </h4>
        <div className="grid grid-cols-4 gap-4">
          {Object.entries(analytics.contentTypePreferences).map(([type, count]) => (
            <div key={type} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {count}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                {type}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Student Analytics Tab
const StudentAnalyticsTab = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [sortBy, setSortBy] = useState('timeSpent'); // timeSpent, interactions, name

  useEffect(() => {
    loadStudentData();
  }, []);

  const loadStudentData = async () => {
    try {
      setLoading(true);
      const sessionsRef = collection(db, 'unschooling_sessions');
      const sessionsSnap = await getDocs(sessionsRef);
      
      const studentsList = [];
      
      for (const docSnap of sessionsSnap.docs) {
        const sessionData = docSnap.data();
        
        // Get user data
        try {
          const userRef = doc(db, 'users', sessionData.userId);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.exists() ? userDoc.data() : null;
          
          // Analyze interactions
          const interactions = sessionData.interactions || [];
          const categoryBreakdown = {};
          const topicBreakdown = {};
          const contentCompleted = [];
          const contentTypeBreakdown = {};
          const quizAnswers = [];
          let quizCorrect = 0;
          let quizTotal = 0;
          
          interactions.forEach(interaction => {
            // Category breakdown
            if (interaction.category) {
              categoryBreakdown[interaction.category] = (categoryBreakdown[interaction.category] || 0) + 1;
            }
            
            // Topic breakdown
            if (interaction.topic) {
              topicBreakdown[interaction.topic] = (topicBreakdown[interaction.topic] || 0) + 1;
            }
            
            // Content completed
            if (interaction.type === 'content_interaction' && interaction.contentTitle) {
              contentCompleted.push({
                title: interaction.contentTitle,
                type: interaction.contentType,
                category: interaction.category,
                topic: interaction.topic,
                timestamp: interaction.timestamp
              });
            }
            
            // Quiz answers
            if (interaction.type === 'quiz_answer') {
              quizAnswers.push({
                question: interaction.question,
                correct: interaction.correct,
                category: interaction.category,
                topic: interaction.topic,
                timestamp: interaction.timestamp
              });
              quizTotal++;
              if (interaction.correct) quizCorrect++;
            }
            
            // Content type breakdown
            if (interaction.contentType) {
              contentTypeBreakdown[interaction.contentType] = (contentTypeBreakdown[interaction.contentType] || 0) + 1;
            }
          });
          
          // Calculate top interests (categories with most time)
          const topCategories = Object.entries(categoryBreakdown)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([cat, count]) => ({ category: cat, count }));
          
          const topTopics = Object.entries(topicBreakdown)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([topic, count]) => ({ topic, count }));
          
          // Calculate days active
          const startDate = new Date(sessionData.startDate);
          const daysElapsed = Math.floor((Date.now() - startDate.getTime()) / (24 * 60 * 60 * 1000));
          
          studentsList.push({
            id: docSnap.id,
            userId: sessionData.userId,
            userName: userData?.displayName || userData?.name || 'Unknown User',
            userEmail: userData?.email || '',
            status: sessionData.status || 'active',
            startDate: sessionData.startDate,
            endDate: sessionData.endDate,
            daysElapsed,
            daysRemaining: Math.max(0, 14 - daysElapsed),
            totalTimeSpent: sessionData.totalTimeSpent || 0,
            totalInteractions: interactions.length,
            categoryBreakdown,
            topicBreakdown,
            topCategories,
            topTopics,
            contentCompleted,
            contentTypeBreakdown,
            quizAnswers,
            quizCorrect,
            quizTotal,
            quizAccuracy: quizTotal > 0 ? Math.round((quizCorrect / quizTotal) * 100) : 0,
            uniqueCategories: Object.keys(categoryBreakdown).length,
            uniqueTopics: Object.keys(topicBreakdown).length
          });
        } catch (userError) {
          console.error('Error fetching user data:', userError);
        }
      }
      
      setStudents(studentsList);
    } catch (error) {
      console.error('Error loading student data:', error);
      toast.error('Failed to load student data');
    } finally {
      setLoading(false);
    }
  };

  // Filter students based on search
  const filteredStudents = students.filter(student => 
    student.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.userEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort students
  const sortedStudents = [...filteredStudents].sort((a, b) => {
    switch (sortBy) {
      case 'timeSpent':
        return b.totalTimeSpent - a.totalTimeSpent;
      case 'interactions':
        return b.totalInteractions - a.totalInteractions;
      case 'name':
        return a.userName.localeCompare(b.userName);
      default:
        return 0;
    }
  });

  const exportStudentReport = (student) => {
    const report = {
      student: {
        name: student.userName,
        email: student.userEmail,
        sessionId: student.id
      },
      session: {
        status: student.status,
        startDate: student.startDate,
        endDate: student.endDate,
        daysElapsed: student.daysElapsed,
        daysRemaining: student.daysRemaining
      },
      engagement: {
        totalTimeSpent: `${Math.floor(student.totalTimeSpent / 60)} hours ${student.totalTimeSpent % 60} minutes`,
        totalInteractions: student.totalInteractions,
        uniqueCategories: student.uniqueCategories,
        uniqueTopics: student.uniqueTopics,
        contentCompleted: student.contentCompleted.length
      },
      topInterests: {
        categories: student.topCategories,
        topics: student.topTopics
      },
      categoryBreakdown: student.categoryBreakdown,
      contentTypePreferences: student.contentTypeBreakdown,
      contentCompleted: student.contentCompleted
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unschooling-report-${student.userName.replace(/\s+/g, '-')}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="text-gray-600 dark:text-gray-400 mt-4">Loading student data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Search and Sort */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Student-Wise Analytics
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {filteredStudents.length} students tracked
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
          >
            <option value="timeSpent">Sort by Time</option>
            <option value="interactions">Sort by Interactions</option>
            <option value="name">Sort by Name</option>
          </select>
        </div>
      </div>

      {/* Students List */}
      {sortedStudents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <Users className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Students Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery ? 'Try adjusting your search' : 'No students have started unschooling sessions yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedStudents.map((student) => {
            const isExpanded = expandedStudent === student.id;
            const totalInteractions = student.totalInteractions;
            const topCategory = student.topCategories[0];
            
            return (
              <div
                key={student.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                {/* Student Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {student.userName}
                        </h4>
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          student.status === 'active' 
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : student.status === 'completed'
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                        }`}>
                          {student.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {student.userEmail}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => exportStudentReport(student)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                        title="Download Report"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={() => setExpandedStudent(isExpanded ? null : student.id)}
                        className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                      <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                        {Math.floor(student.totalTimeSpent / 60)}h {student.totalTimeSpent % 60}m
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Time Spent</div>
                    </div>
                    
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                      <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                        {student.contentCompleted.length}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Content Completed</div>
                    </div>
                    
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                      <div className="text-xl font-bold text-green-600 dark:text-green-400">
                        {student.uniqueCategories}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Categories</div>
                    </div>
                    
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                      <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                        {student.uniqueTopics}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Topics</div>
                    </div>
                    
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
                      <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400">
                        {student.quizAccuracy}%
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Quiz Accuracy</div>
                    </div>
                    
                    <div className="bg-pink-50 dark:bg-pink-900/20 rounded-lg p-3">
                      <div className="text-xl font-bold text-pink-600 dark:text-pink-400">
                        {student.daysElapsed}/{14}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Days Progress</div>
                    </div>
                  </div>
                  
                  {/* Top Interest Preview */}
                  {topCategory && (
                    <div className="mt-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        <span className="font-semibold">Top Interest:</span> {topCategory.category} 
                        <span className="text-gray-500 ml-2">({Math.round((topCategory.count / totalInteractions) * 100)}% of activity)</span>
                      </p>
                    </div>
                  )}
                  
                  {/* Quiz Performance Preview */}
                  {student.quizTotal > 0 && (
                    <div className="mt-2 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        <span className="font-semibold">Quiz Performance:</span> {student.quizCorrect}/{student.quizTotal} correct
                        <span className="text-gray-500 ml-2">({student.quizAccuracy}% accuracy)</span>
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900/50 space-y-6">
                    {/* Category Breakdown */}
                    <div>
                      <h5 className="font-semibold text-gray-900 dark:text-white mb-3">
                        Category Distribution
                      </h5>
                      <div className="space-y-2">
                        {Object.entries(student.categoryBreakdown)
                          .sort((a, b) => b[1] - a[1])
                          .map(([category, count]) => {
                            const percentage = Math.round((count / totalInteractions) * 100);
                            return (
                              <div key={category}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                                    {category}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    {count} interactions ({percentage}%)
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                  <div
                                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                    
                    {/* Top Topics */}
                    <div>
                      <h5 className="font-semibold text-gray-900 dark:text-white mb-3">
                        Top Topics Explored
                      </h5>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {student.topTopics.map((item, index) => (
                          <div
                            key={item.topic}
                            className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                #{index + 1}
                              </span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {item.topic}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {item.count} interactions
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Content Completed */}
                    <div>
                      <h5 className="font-semibold text-gray-900 dark:text-white mb-3">
                        Content Completed ({student.contentCompleted.length})
                      </h5>
                      {student.contentCompleted.length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {student.contentCompleted.map((content, index) => (
                            <div
                              key={index}
                              className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h6 className="font-medium text-gray-900 dark:text-white text-sm">
                                    {content.title}
                                  </h6>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded">
                                      {content.category}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {content.topic}
                                    </span>
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 rounded capitalize">
                                      {content.type}
                                    </span>
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400">
                                  {new Date(content.timestamp).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No content completed yet
                        </p>
                      )}
                    </div>
                    
                    {/* Content Type Preferences */}
                    {Object.keys(student.contentTypeBreakdown).length > 0 && (
                      <div>
                        <h5 className="font-semibold text-gray-900 dark:text-white mb-3">
                          Content Type Preferences
                        </h5>
                        <div className="grid grid-cols-4 gap-3">
                          {Object.entries(student.contentTypeBreakdown).map(([type, count]) => (
                            <div
                              key={type}
                              className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 text-center"
                            >
                              <div className="text-xl font-bold text-gray-900 dark:text-white">
                                {count}
                              </div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                                {type}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Quiz Performance */}
                    {student.quizAnswers && student.quizAnswers.length > 0 && (
                      <div>
                        <h5 className="font-semibold text-gray-900 dark:text-white mb-3">
                          Quiz Performance ({student.quizCorrect}/{student.quizTotal} correct - {student.quizAccuracy}%)
                        </h5>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {student.quizAnswers.map((quiz, index) => (
                            <div
                              key={index}
                              className={`rounded-lg p-3 border ${
                                quiz.correct
                                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h6 className={`font-medium text-sm ${
                                    quiz.correct
                                      ? 'text-green-900 dark:text-green-100'
                                      : 'text-red-900 dark:text-red-100'
                                  }`}>
                                    {quiz.question}
                                  </h6>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      quiz.correct
                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                    }`}>
                                      {quiz.correct ? '✓ Correct' : '✗ Incorrect'}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {quiz.category} • {quiz.topic}
                                    </span>
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400">
                                  {new Date(quiz.timestamp).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Settings Tab
const SettingsTab = () => {
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
          Session Configuration
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Current session duration: 14 days
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          To change session duration, update the code in <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">UnschoolingHub.js</code>
        </p>
      </div>

      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
          Categories
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Current categories:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li>• ⚛️ Sciences</li>
          <li>• 🎨 Arts & Design</li>
          <li>• 💻 Technology</li>
          <li>• 🌍 Humanities</li>
          <li>• ❤️ Life Skills</li>
          <li>• 💡 Creative</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
          To add/modify categories, update <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">ExploreSpace.js</code>
        </p>
      </div>

      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
          Content Guidelines
        </h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <li>• Keep content duration between 10-30 minutes</li>
          <li>• Use emojis for thumbnails (more engaging)</li>
          <li>• Ensure content is age-appropriate (13+)</li>
          <li>• Mix content types (40% video, 30% interactive, 20% article, 10% project)</li>
          <li>• Add descriptive tags for better discovery</li>
        </ul>
      </div>
    </div>
  );
};

export default UnschoolingManagement;
